import { useState, useEffect, useCallback } from 'react';

export const COOKIE_CONSENT_KEY = 'cookie-consent';

export type ConsentType = 'all' | 'necessary' | null;

export const useCookieConsent = () => {
  const [consent, setConsent] = useState<ConsentType>(() => {
    if (typeof window === 'undefined') return null;
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    return (stored as ConsentType) || null;
  });

  const hasAnalyticsConsent = consent === 'all';

  const acceptAll = useCallback(() => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'all');
    setConsent('all');
    window.dispatchEvent(new CustomEvent('cookie-consent-change', { detail: 'all' }));
  }, []);

  const acceptNecessary = useCallback(() => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'necessary');
    setConsent('necessary');
    clearAnalyticsCookies();
    window.dispatchEvent(new CustomEvent('cookie-consent-change', { detail: 'necessary' }));
  }, []);

  // Listen for consent changes from other components
  useEffect(() => {
    const handleConsentChange = (e: CustomEvent<ConsentType>) => {
      setConsent(e.detail);
    };

    window.addEventListener('cookie-consent-change', handleConsentChange as EventListener);
    return () => {
      window.removeEventListener('cookie-consent-change', handleConsentChange as EventListener);
    };
  }, []);

  return {
    consent,
    hasAnalyticsConsent,
    acceptAll,
    acceptNecessary,
  };
};

// Helper function to check consent status
export const getAnalyticsConsent = (): boolean => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(COOKIE_CONSENT_KEY) === 'all';
};

export const OPEN_COOKIE_SETTINGS_EVENT = 'open-cookie-settings';

// Reopen the cookie banner so the user can change or withdraw consent (GDPR art. 7.3)
export const openCookieSettings = () => {
  window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS_EVENT));
};

// Remove Google Analytics cookies (_ga, _ga_XXXX) on every domain level they may live on
export const clearAnalyticsCookies = () => {
  if (typeof document === 'undefined') return;
  const host = window.location.hostname;
  const domains = ['', host, `.${host}`, `.${host.split('.').slice(-2).join('.')}`];
  document.cookie
    .split(';')
    .map((c) => c.split('=')[0].trim())
    .filter((name) => name === '_ga' || name.startsWith('_ga_') || name === '_gid')
    .forEach((name) => {
      domains.forEach((d) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=${d}` : ''}`;
      });
    });
};
